import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../stores/auth.store';
import { getCollectionSites } from '../services/collection-site.service';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserSchema } from '../types/auth.type';
import { useOrganizationStore } from '../stores/organization.store';
import { OrganizationType, isSiteOperationsOrgType } from '../types/organization.type';
import { useAuthPermissions } from '../hooks/useAuthPermissions';
import {
  PERM_USER_LIST,
  USER_MANAGEMENT_ROLES,
  type RoleOrgType,
  isRoleOrgType,
  inferRoleLabel,
} from '../auth';
import { permissionSummaryLabel } from '../utils/permissions.helpers';
import {
  ROLE_FILTER_OPTIONS,
  buildSiteIdToLabelMap,
  formatSitesListCell,
} from '../utils/users.helpers';
import UserDetailsDialog from '../components/users/UserDetailsDialog';
import PageTitleIcon from '../components/PageTitleIcon';
import { SetupCoachmark } from '../components/selfSetup/SetupCoachmark';
import { useTranslation } from 'react-i18next';
import { HELP_GUIDE_ADD_USER, HELP_GUIDE_USERS_OVERVIEW } from '../constants/knowledgeBaseTryIt';
import { helpI18n } from '../i18n/help';
import {
  clearHelpGuideActive,
  markHelpGuideActive,
} from '../utils/helpGuideSession';

const Users: React.FC = () => {
  const { t } = useTranslation();
  const { t: tHelp } = useTranslation('translation', { i18n: helpI18n });
  const navigate = useNavigate();
  const location = useLocation();
  const { user, error: authError, isLoading: authLoading, getOrganizationUsers } = useAuthStore();
  const { selectedOrganization, setSelectedOrganization } = useOrganizationStore();
  const {
    can,
    isReadOnly,
    orgId: selectedOrgId,
    canManageTeam,
  } = useAuthPermissions();
  const [helpAddGuide, setHelpAddGuide] = useState(false);
  const [helpUsersOverview, setHelpUsersOverview] = useState(false);
  // Add-button tip only on the list page (not while the form is open)
  const showUsersAddTour =
    helpAddGuide && location.pathname === '/users';

  const finishTeamTour = () => {
    setHelpAddGuide(false);
    clearHelpGuideActive();
  };

  const finishUsersOverviewGuide = () => {
    setHelpUsersOverview(false);
    clearHelpGuideActive();
  };

  const openAddUser = () => {
    if (helpAddGuide) {
      navigate(`/users/new?helpGuide=${HELP_GUIDE_ADD_USER}`);
      return;
    }
    navigate('/users/new');
  };

  useEffect(() => {
    if (location.pathname !== '/users') return;
    const guide = new URLSearchParams(location.search).get('helpGuide');
    if (guide === HELP_GUIDE_ADD_USER) {
      markHelpGuideActive(guide);
      setHelpAddGuide(true);
      navigate({ pathname: '/users', search: '' }, { replace: true });
      return;
    }
    if (guide === HELP_GUIDE_USERS_OVERVIEW) {
      markHelpGuideActive(guide);
      setHelpUsersOverview(true);
      navigate({ pathname: '/users', search: '' }, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  useEffect(() => {
    if (!helpAddGuide) return;
    window.requestAnimationFrame(() => {
      document
        .getElementById('help-add-user-button')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [helpAddGuide]);

  useEffect(() => {
    if (!helpUsersOverview) return;
    window.requestAnimationFrame(() => {
      document
        .getElementById('help-users-search')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [helpUsersOverview]);

  const orgType = selectedOrganization?.organization_type;
  const roleOrgType: RoleOrgType | null =
    orgType && isRoleOrgType(orgType) ? orgType : null;
  const isSiteOpsOrg = orgType
    ? isSiteOperationsOrgType(orgType)
    : false;

  const canListUsers = can(
    selectedOrgId ? PERM_USER_LIST(selectedOrgId) : 'recyclee.*.*.user.list',
    USER_MANAGEMENT_ROLES,
  );
  const canWriteUsers = () => {
    if (!selectedOrgId) return false;
    return canManageTeam(selectedOrgId);
  };

  const [users, setUsers] = useState<UserSchema[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterSearch, setFilterSearch] = useState('');
  const [filterRole, setFilterRole] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity?: 'success' | 'error';
  }>({ open: false, message: '' });
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewUser, setViewUser] = useState<UserSchema | null>(null);
  const [tableSiteIdToLabel, setTableSiteIdToLabel] = useState<Record<string, string>>({});

  const canAccess = canListUsers;
  const hasRedirectedRef = useRef(false);

  useEffect(() => {
    const state = location.state as { snackbar?: string } | null;
    if (state?.snackbar) {
      setSnackbar({ open: true, message: state.snackbar, severity: 'success' });
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  useEffect(() => {
    if (authLoading || user === null || hasRedirectedRef.current) return;
    if (!selectedOrganization?.id || !canAccess) {
      hasRedirectedRef.current = true;
      navigate('/');
    }
  }, [selectedOrganization?.id, canAccess, navigate, authLoading, user]);

  useEffect(() => {
    if (authError) setError(authError);
  }, [authError]);

  useEffect(() => {
    const storedOrg = sessionStorage.getItem('selectedOrganization');
    if (storedOrg) {
      try {
        setSelectedOrganization(JSON.parse(storedOrg));
      } catch {
        // ignore
      }
    }
  }, [setSelectedOrganization]);

  useEffect(() => {
    if (selectedOrganization?.id) loadUsers();
  }, [selectedOrganization?.id]);

  useEffect(() => {
    const orgId = selectedOrganization?.id;
    if (!orgId || !isSiteOpsOrg) {
      setTableSiteIdToLabel({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await getCollectionSites({ organization_id: orgId });
        const data = response as { result?: { id: string }[] };
        const list = Array.isArray(data.result) ? data.result : [];
        if (!cancelled) {
          setTableSiteIdToLabel(buildSiteIdToLabelMap(list as Parameters<typeof buildSiteIdToLabelMap>[0]));
        }
      } catch {
        if (!cancelled) setTableSiteIdToLabel({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedOrganization?.id, isSiteOpsOrg]);

  const loadUsers = async () => {
    if (!selectedOrganization?.id) return;
    try {
      setIsLoading(true);
      const fetchedUsers = await getOrganizationUsers(selectedOrganization.id);
      setUsers(fetchedUsers);
    } catch {
      setError(t('users.failedToFetch'));
    } finally {
      setIsLoading(false);
    }
  };

  const filteredUsers = useMemo(() => {
    let list = users;
    const search = filterSearch.trim().toLowerCase();
    if (search) {
      list = list.filter((u) => {
        const username = (u.username ?? '').toLowerCase();
        const first = (u.data?.first_name ?? '').toLowerCase();
        const last = (u.data?.last_name ?? '').toLowerCase();
        const phone = (u.phone ?? '').toLowerCase();
        return (
          username.includes(search) ||
          first.includes(search) ||
          last.includes(search) ||
          phone.includes(search)
        );
      });
    }
    if (filterRole && roleOrgType && selectedOrganization?.id) {
      const orgId = selectedOrganization.id;
      list = list.filter((u) => inferRoleLabel(u, orgId, roleOrgType) === filterRole);
    }
    if (filterStatus) {
      list = list.filter((u) => (u.status ?? 'active') === filterStatus);
    }
    return list;
  }, [users, filterSearch, filterRole, filterStatus, selectedOrganization?.id, roleOrgType]);

  useEffect(() => {
    setPage(0);
  }, [filterSearch, filterRole, filterStatus, selectedOrganization?.id]);

  const paginatedUsers = useMemo(
    () =>
      filteredUsers.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      ),
    [filteredUsers, page, rowsPerPage],
  );

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const goToEdit = (u: UserSchema) => {
    const id = u.id ?? u.sub;
    if (id) navigate(`/users/${id}/edit`);
  };

  if (user !== null && !canAccess) {
    return (
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 600, color: "text.primary", mb: 2 }}>
          {t('users.title')}
        </Typography>
        <Alert severity="warning">
          {t('users.noPermission')}
        </Alert>
      </Box>
    );
  }

  const usersLoading = isLoading || authLoading;
  const userColumnCount = isSiteOpsOrg ? 8 : 7;

  return (
    <Box>
      <Paper
        elevation={2}
        sx={{
          mb: 3,
          p: 3,
          borderRadius: '16px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
            mb: 2.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <PageTitleIcon icon={<PeopleIcon />} />
            <Typography variant="h4" sx={{ fontWeight: 600, color: "text.primary" }}>
              {t('users.title')}
            </Typography>
          </Box>
          {(canWriteUsers() && !isReadOnly) || helpAddGuide ? (
            <SetupCoachmark
              open={showUsersAddTour}
              title={tHelp('knowledgeBase.addUserGuideTitle')}
              subtitle={tHelp('knowledgeBase.addUserGuideSubtitle')}
              onDismiss={finishTeamTour}
            >
              <Button
                id="help-add-user-button"
                variant="contained"
                size="medium"
                startIcon={<AddIcon />}
                onClick={openAddUser}
                sx={{
                  textTransform: 'none',
                  borderRadius: '12px',
                  px: 2.5,
                  py: 1,
                  fontWeight: 600,
                  boxShadow: 'none',
                  whiteSpace: 'nowrap',
                  '&:hover': { boxShadow: 1 },
                }}
              >
                {t('users.addUser')}
              </Button>
            </SetupCoachmark>
          ) : null}
        </Box>

        <SetupCoachmark
          open={helpUsersOverview}
          title={tHelp('knowledgeBase.usersOverviewTitle')}
          subtitle={tHelp('knowledgeBase.usersOverviewSubtitle')}
          onDismiss={finishUsersOverviewGuide}
          onNext={finishUsersOverviewGuide}
          nextLabel={t('selfSetup.tourDone')}
          fullWidth
        >
          <Box
            id="help-users-search"
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              alignItems: 'center',
              width: '100%',
            }}
          >
        <TextField
          size="small"
          placeholder={t('users.searchPlaceholder')}
          value={filterSearch}
          onChange={(e) => setFilterSearch(e.target.value)}
          sx={{ minWidth: 220 }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>{t('users.role')}</InputLabel>
          <Select value={filterRole} label={t('users.role')} onChange={(e) => setFilterRole(e.target.value)}>
            <MenuItem value="">{t('common.all')}</MenuItem>
            {ROLE_FILTER_OPTIONS.map((label) => (
              <MenuItem key={label} value={label}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>{t('users.status')}</InputLabel>
          <Select
            value={filterStatus}
            label={t('users.status')}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            <MenuItem value="active">{t('common.active')}</MenuItem>
            <MenuItem value="inactive">{t('common.inactive')}</MenuItem>
          </Select>
        </FormControl>
          </Box>
        </SetupCoachmark>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper elevation={2} sx={{ borderRadius: '16px', overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{t('users.columns.username')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('users.columns.name')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('users.columns.phone')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('users.columns.role')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('users.columns.access')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('users.columns.status')}</TableCell>
                {isSiteOpsOrg && <TableCell sx={{ fontWeight: 600 }}>{t('users.columns.branchSites')}</TableCell>}
                <TableCell sx={{ fontWeight: 600 }} align="right">{t('users.columns.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {usersLoading &&
                Array.from({ length: rowsPerPage > 5 ? 5 : rowsPerPage }).map((_, i) => (
                  <TableRow key={`user-skel-${i}`}>
                    {Array.from({ length: userColumnCount }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

              {!usersLoading && paginatedUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={userColumnCount} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary" fontWeight={500}>
                      {t('users.noUsers')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {!usersLoading &&
                paginatedUsers.map((u) => {
              const userId = u.id ?? u.sub;
              const name =
                [u.data?.first_name, u.data?.last_name].filter(Boolean).join(' ') || '—';
              const orgId = selectedOrganization?.id;
              const sitesForOrg =
                orgId && u.data?.sites ? u.data.sites[orgId] : undefined;
              const sitesText = formatSitesListCell(sitesForOrg, tableSiteIdToLabel);
              const roleLabel =
                orgId && roleOrgType ? inferRoleLabel(u, orgId, roleOrgType) : 'Unknown';
              const accessLabel =
                orgId && orgType ? permissionSummaryLabel(u, orgId, orgType) : '—';
              return (
                <TableRow key={userId ?? u.username} hover>
                  <TableCell>{u.username}</TableCell>
                  <TableCell>{name}</TableCell>
                  <TableCell>{u.phone ?? '—'}</TableCell>
                  <TableCell>{roleLabel}</TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {accessLabel}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={u.status === 'inactive' ? t('common.inactive') : t('common.active')}
                      color={u.status === 'inactive' ? 'default' : 'success'}
                    />
                  </TableCell>
                  {isSiteOpsOrg && (
                    <TableCell sx={{ maxWidth: 280 }}>
                      <Tooltip
                        title={sitesText.full !== sitesText.display ? sitesText.full : ''}
                        disableHoverListener={sitesText.full === sitesText.display}
                      >
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {sitesText.display}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                  )}
                  <TableCell align="right">
                    <IconButton
                      onClick={() => {
                        setViewUser(u);
                        setViewDialogOpen(true);
                      }}
                      size="small"
                      title={t('users.viewDetails')}
                    >
                      <VisibilityIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => goToEdit(u)}
                      size="small"
                      title={t('common.edit')}
                      disabled={!canWriteUsers() || isReadOnly}
                    >
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredUsers.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage={t('common.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => t('common.rangeOf', { from, to, count })}
        />
      </Paper>

      <UserDetailsDialog
        open={viewDialogOpen}
        user={viewUser}
        organization={selectedOrganization}
        roleOrgType={roleOrgType}
        canWrite={canWriteUsers() && !isReadOnly}
        onClose={() => {
          setViewDialogOpen(false);
          setViewUser(null);
        }}
        onEdit={goToEdit}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity ?? 'success'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Users;
